import React, { useEffect, useMemo, useState } from "react";
import { Mail, Phone, Trash2, RefreshCw, Filter, Search, Inbox } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const STATUSES = ["New", "In Progress", "Closed"];
const STATUS_STYLES = {
  New: "bg-blue-100 text-blue-800 ring-blue-200",
  "In Progress": "bg-amber-100 text-amber-800 ring-amber-200",
  Closed: "bg-emerald-100 text-emerald-800 ring-emerald-200",
};

export default function InstituteAdmissionInquiries() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const { data } = await api.get("/institute/admission-inquiries", { params });
      setItems(data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load inquiries");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter(
      (i) =>
        i.candidate_name?.toLowerCase().includes(q) ||
        i.mobile?.includes(q) ||
        (i.email || "").toLowerCase().includes(q) ||
        (i.department_name || "").toLowerCase().includes(q) ||
        (i.inquiry_details || "").toLowerCase().includes(q)
    );
  }, [items, search]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    STATUSES.forEach((s) => {
      c[s] = items.filter((i) => i.status === s).length;
    });
    return c;
  }, [items]);

  const changeStatus = async (id, status) => {
    const prev = items;
    setItems((arr) => arr.map((i) => (i.id === id ? { ...i, status } : i)));
    try {
      await api.patch(`/institute/admission-inquiries/${id}`, { status });
      toast.success(`Marked as ${status}`);
    } catch (e) {
      setItems(prev);
      toast.error(e?.response?.data?.detail || "Could not update status");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/institute/admission-inquiries/${confirmDelete.id}`);
      setItems((arr) => arr.filter((i) => i.id !== confirmDelete.id));
      toast.success("Inquiry deleted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete");
    } finally {
      setConfirmDelete(null);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString(undefined, {
        day: "numeric",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="p-6" data-testid="institute-admission-inquiries">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#1c1a17]">Admission Inquiries</h1>
          <p className="text-sm text-[#6b655b] mt-1">
            All inquiries submitted from your public website's "Contact Our Admission Team" form.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 bg-[#234333] hover:bg-[#1b3328] text-white px-4 py-2 rounded-md text-sm"
          data-testid="ai-refresh"
        >
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        <Stat label="Total" value={counts.all} testid="ai-stat-total" />
        <Stat label="New" value={counts.New} tone="blue" testid="ai-stat-new" />
        <Stat label="In Progress" value={counts["In Progress"]} tone="amber" testid="ai-stat-progress" />
        <Stat label="Closed" value={counts.Closed} tone="green" testid="ai-stat-closed" />
      </div>

      {/* Filters */}
      <div className="bg-white rounded-md border border-[#e5e0d8] p-3 mb-4 flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-xs text-[#6b655b]">
          <Filter size={13} /> Status:
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-[#d6d2ca] rounded-md px-2.5 py-1.5 text-sm bg-white"
          data-testid="ai-status-filter"
        >
          <option value="">All ({counts.all})</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {s} ({counts[s] || 0})
            </option>
          ))}
        </select>
        <div className="flex items-center gap-2 ml-auto">
          <Search size={13} className="text-[#6b655b]" />
          <input
            type="search"
            placeholder="Search name / mobile / email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="border border-[#d6d2ca] rounded-md px-2.5 py-1.5 text-sm w-64"
            data-testid="ai-search"
          />
        </div>
      </div>

      {/* List */}
      <div className="bg-white rounded-md border border-[#e5e0d8] overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-[#6b655b]">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-[#6b655b] flex flex-col items-center gap-2">
            <Inbox size={28} className="text-[#bfb8a8]" />
            {items.length === 0 ? "No inquiries yet." : "No inquiries match your filters."}
          </div>
        ) : (
          <ul className="divide-y divide-[#eee9de]">
            {filtered.map((i) => (
              <li
                key={i.id}
                className="p-4 sm:p-5 hover:bg-[#fbf9f4]"
                data-testid={`ai-row-${i.id}`}
              >
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-semibold text-[#1c1a17]">{i.candidate_name}</div>
                      <span
                        className={`inline-flex items-center text-[10px] tracking-wider uppercase px-2 py-0.5 rounded-full ring-1 font-semibold ${STATUS_STYLES[i.status] || ""}`}
                      >
                        {i.status}
                      </span>
                    </div>
                    <div className="mt-1 text-xs text-[#6b655b]">{formatDate(i.created_at)}</div>
                    <div className="mt-2 flex items-center gap-4 flex-wrap text-sm text-[#3d3a35]">
                      <a
                        href={`tel:+91${i.mobile}`}
                        className="inline-flex items-center gap-1.5 hover:text-blue-700"
                      >
                        <Phone size={13} /> +91 {i.mobile}
                      </a>
                      {i.email && (
                        <a
                          href={`mailto:${i.email}`}
                          className="inline-flex items-center gap-1.5 hover:text-blue-700"
                        >
                          <Mail size={13} /> {i.email}
                        </a>
                      )}
                      {i.department_name && (
                        <span className="text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-sm bg-blue-50 text-blue-800">
                          {i.department_name}
                        </span>
                      )}
                    </div>
                    {i.inquiry_details && (
                      <p className="mt-2 text-sm text-[#4a463f] whitespace-pre-wrap leading-relaxed">
                        {i.inquiry_details}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={i.status}
                      onChange={(e) => changeStatus(i.id, e.target.value)}
                      className="border border-[#d6d2ca] rounded-md px-2 py-1.5 text-sm bg-white"
                      data-testid={`ai-status-${i.id}`}
                    >
                      {STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={() => setConfirmDelete(i)}
                      className="p-2 text-rose-600 hover:bg-rose-50 rounded-md"
                      title="Delete inquiry"
                      data-testid={`ai-delete-${i.id}`}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this inquiry?</AlertDialogTitle>
            <AlertDialogDescription>
              The inquiry from <strong>{confirmDelete?.candidate_name}</strong> ({confirmDelete?.mobile}) will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={doDelete}
              className="bg-rose-600 hover:bg-rose-700"
              data-testid="ai-confirm-delete"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Stat({ label, value, tone = "slate", testid }) {
  const tones = {
    slate: "from-slate-100 to-slate-200 text-slate-900",
    blue: "from-blue-50 to-blue-100 text-blue-900",
    amber: "from-amber-50 to-amber-100 text-amber-900",
    green: "from-emerald-50 to-emerald-100 text-emerald-900",
  };
  return (
    <div
      className={`bg-gradient-to-br ${tones[tone]} rounded-md p-3`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold opacity-75">
        {label}
      </div>
      <div className="text-2xl font-serif mt-0.5">{value}</div>
    </div>
  );
}
